/* eslint-disable */
import React, { useEffect, useState, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import * as validate from '@ownportal/customer-validations';
import Loader from '../components/common/loader/loader';
import { useMyVW } from '../../context/myVWContext';
import { SessionContext } from '../../context/sessionContext';
import { register as registerService, createAccount } from '../services/myVW';
import { getChallenegeQuestionOptions, getFormattedSSN, restrictKeyPressHandler, isValidAccountNumber } from '../../utilities';
import SelectField from '../components/common/select-field/selectField';
import i18n from '../../localization/i18n';
import * as rk from '../../localization/resourceKeys';
import { StepLayout } from '../components/common/steplayout/StepLayout';
import { MyVWMarketingBanner } from '../components/common/banner/MyVWMarketingBanner';
import BannerNotification from '../components/common/banner/bannerNotification';
import FormField from '../components/common/form-field/formField';
import CaptchaGate from '../components/common/captcha/CaptchaGate';
import { useMyVWQueryParams } from '../hooks/useMyVWQueryParams';
import Logger from '../../logger';
import MyVWPaperlessConsent from './myVWPaperlessConsent';

type RegisterStep = 'form' | 'credentials' | 'consent';

const MyVWRegister: React.FC = () => {
  const navigate = useNavigate();
  const { applyAuthState, partyId } = useMyVW();
  const { token, brand, country, buildSearch } = useMyVWQueryParams();
  const { sessionDetails } = useContext(SessionContext)!;
  const myVWSessionDetails = { ...sessionDetails, brand, country };
  const { register, errors, clearErrors, trigger } = useForm({});
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const emailParam = searchParams.get('email');
  const stepParam = searchParams.get('step');
  const [step, setStep] = useState<RegisterStep>(stepParam as RegisterStep || 'form');

  // ── Step: verify identity ─────────────────────────────────────────────────
  const [accountNumber, setAccountNumber] = useState('');
  const [rawSsn, setRawSsn] = useState('');
  const [maskedSsn, setMaskedSsn] = useState('');
  const [email, setEmail] = useState(emailParam || '');
  const [confirmEmail, setConfirmEmail] = useState(emailParam || '');
  const [captchaCompleted, setCaptchaCompleted] = useState(
    process.env.REACT_APP_AWS_ENV === 'dev' || process.env.NODE_ENV === 'test',
  );
  const [verifyEnabled, setVerifyEnabled] = useState(false);

  // ── Step: credentials ─────────────────────────────────────────────────────
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [challengeQuestion, setChallengeQuestion] = useState('');
  const [challengeAnswer, setChallengeAnswer] = useState('');
  const [credentialsEnabled, setCredentialsEnabled] = useState(false);
  const [challengeOptions] = useState(() => getChallenegeQuestionOptions(brand || 'VW'));
  const [usernameConflict, setUsernameConflict] = useState(false);

  // ── Step: consent ─────────────────────────────────────────────────────────
  const [agreeConsent, setAgreeConsent] = useState(false);

  // ── Shared ────────────────────────────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [verifyErrorBanner, setVerifyErrorBanner] = useState<{ title?: string; message: string } | null>(null);

  const normalizeEmail = (value: string) => value.trim().toLowerCase();
  const restrictAccountKeyPress = restrictKeyPressHandler(/^[0-9]*$/i);

  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;
    const emailFormatValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    const emailsMatch = normalizeEmail(email) === normalizeEmail(confirmEmail);
    setVerifyEnabled(!!(
      accountNumber
      && isValidAccountNumber(accountNumber)
      && rawSsn.length === 9
      && email
      && confirmEmail
      && emailFormatValid
      && emailsMatch
      && !hasErrors
      && captchaCompleted
    ));
  }, [accountNumber, rawSsn, email, confirmEmail, errors, captchaCompleted]);

  useEffect(() => {
    const hasErrors = Object.keys(errors).length > 0;
    setCredentialsEnabled(!!(username && password && confirmPassword && challengeQuestion && challengeAnswer && !hasErrors));
  }, [username, password, confirmPassword, challengeQuestion, challengeAnswer, errors]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSsnKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!/^\d*$/.test(e.key) && e.key !== 'Backspace') {
      e.preventDefault();
      return;
    }

    let cursorPosition = (e.target as HTMLInputElement).selectionStart ?? 0;

    if (cursorPosition >= 3 && cursorPosition < 7) {
      cursorPosition = cursorPosition - 1;
    } else if (cursorPosition >= 7) {
      cursorPosition = cursorPosition - 2;
    }

    let newRawSsn: string;

    if (e.key === 'Backspace') {
      if (cursorPosition === 0) return;
      newRawSsn = rawSsn.substring(0, cursorPosition - 1) + rawSsn.substring(cursorPosition);
      cursorPosition = cursorPosition - 1;

      if (cursorPosition >= 2 && cursorPosition < 5) {
        cursorPosition += 1;
      } else if (cursorPosition >= 5) {
        cursorPosition += 2;
      }
    } else {
      if (rawSsn.length === 9) return;
      newRawSsn = rawSsn.substring(0, cursorPosition) + e.key + rawSsn.substring(cursorPosition);
      cursorPosition = cursorPosition + 1;

      if (cursorPosition >= 3 && cursorPosition < 6) {
        cursorPosition += 1;
      } else if (cursorPosition >= 6) {
        cursorPosition += 2;
      }
    }

    setRawSsn(newRawSsn);
    setMaskedSsn(getFormattedSSN(newRawSsn));

    setTimeout(() => {
      const input = e.target as HTMLInputElement;
      input.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };

  const handleVerify = async () => {
    await trigger('accountNumber');
    await trigger('socialSecurityNumber');
    await trigger('email');
    await trigger('confirmEmail');
    if (Object.keys(errors).length > 0) return;
    setVerifyErrorBanner(null);
    setError(null);
    setLoading(true);
    const result = await registerService(accountNumber, rawSsn, myVWSessionDetails);
    setLoading(false);
    if (result.isAlreadyRegistered) {
      setError(i18n.t(rk.ERROR_MYVW_ALREADY_REGISTERED));
      return;
    }
    if (result.partyId) {
      applyAuthState({ partyId: result.partyId });
    }
    if (!result.isSuccess) {
      Logger.error(
        "myVW Register API Error",
        "Location ==> /myVWRegister",
        result.error,
        myVWSessionDetails,
      );
      setError(result.error ?? i18n.t(rk.ERROR_MYVW_REGISTER_FAILED));
      return;
    }
    setStep('credentials');
  };

  const handleCredentials = async () => {
    await trigger('username');
    await trigger('password');
    await trigger('confirmPassword');
    await trigger('challengeQuestion');
    await trigger('challengeAnswer');
    if (Object.keys(errors).length > 0) return;
    setVerifyErrorBanner(null);
    setUsernameConflict(false);
    setError(null);
    setStep('consent');
  };

  const handleConsent = async () => {
    if (!agreeConsent) return;
    setVerifyErrorBanner(null);
    setError(null);
    setLoading(true);
    const result = await createAccount(
      accountNumber,
      partyId ?? 0,
      email,
      username,
      password,
      challengeQuestion,
      challengeAnswer,
      agreeConsent ?? true,
      myVWSessionDetails,
    );
    if (result.isUsernameConflict) {
      setLoading(false);
      setUsernameConflict(true);
      setStep('credentials');
      return;
    }
    if (!result.isSuccess) {
      setLoading(false);
      setError(result.error ?? i18n.t(rk.ERROR_MYVW_CREATE_ACCOUNT_FAILED));
      return;
    }

    let loginAuthenticatorCodeFromVerify = result.loginAuthenticatorCode ?? '';

    applyAuthState({ email, partyId: partyId ?? undefined });
    navigate('/myVW/mfa' + buildSearch({
      loginAuthenticatorCode: loginAuthenticatorCodeFromVerify,
      isLinked: 'false',
      username,
    }));
  };

  // ── Verify identity step ──────────────────────────────────────────────────

  if (step === 'form') {
    return (
      <StepLayout
        title={<h3 className='u-mb-small'>{i18n.t(rk.TITLE_MYVW_REGISTER_FORM)}</h3>}
        primaryAction={{
          label: i18n.t(rk.BUTTON_CANCEL),
          onClick: () => navigate('/myVW/registration-question' + buildSearch()),
          testId: 'register-form-cancel',
        }}
        secondaryAction={{
          label: i18n.t(rk.BUTTON_CONTINUE),
          onClick: handleVerify,
          disabled: !verifyEnabled,
          testId: 'register-form-submit',
        }}
      >
        {loading && <Loader isLoading />}
        {verifyErrorBanner && (
          <div className="u-mb">
            <BannerNotification notificationStyle="error" title={verifyErrorBanner.title}>
              {verifyErrorBanner.message}
            </BannerNotification>
          </div>
        )}
        <h4 className="u-text-subtitle u-mb-xsmall">{i18n.t(rk.LABEL_MYVW_VEHICLE_INFO)}</h4>

        <p>{i18n.t(rk.TEXT_MYVW_REGISTER_INTRO)}</p>
        <MyVWMarketingBanner />
        {error && <p className="c-error-message u-mt">{error}</p>}
        <p className='u-mt'><b>{i18n.t(rk.TEXT_ALL_FIELDS_REQUIRED)}</b></p>
        <FormField
          id="accountNumber"
          label={i18n.t(rk.LABEL_ACCOUNT_NUMBER)}
          type="text"
          testid="test-account-number-field"
          onChange={(e) => {
            if (e.target.value.length > 10) return;
            setAccountNumber(e.target.value);
            clearErrors('accountNumber');
          }}
          onKeyPress={restrictAccountKeyPress}
          autoCompleteOff
          value={accountNumber}
          fieldRef={register({
            required: i18n.t(rk.ERROR_EMPTY_ACCOUNT_NUMBER),
            validate: {
              validateAccountNumber: (value) =>
                isValidAccountNumber(value) || i18n.t(rk.ERROR_INVALID_ACCNUMBER),
            },
          }) as any}
          onBlur={async () => { await trigger('accountNumber'); }}
          fieldErrors={errors.accountNumber?.message}
          hint={i18n.t(rk.TEXT_ACCOUNT_NUM_HINT)}
        />
        <FormField
          id="socialSecurityNumber"
          label={i18n.t(rk.LABEL_SSN)}
          type="tel"
          testid="test-ssn-field"
          onChange={() => { clearErrors('socialSecurityNumber'); }}
          onKeyDown={handleSsnKeyDown}
          autoCompleteOff
          value={maskedSsn}
          fieldRef={register({ required: i18n.t(rk.ERROR_EMPTY_SSN), validate: { validateSSN: () => rawSsn.length === 9 || i18n.t(rk.ERROR_SSN_FORMAT) } }) as any}
          onBlur={async () => { await trigger('socialSecurityNumber'); }}
          fieldErrors={errors.socialSecurityNumber?.message}
          hint={i18n.t(rk.TEXT_SSN_HINT)}
        />
        <FormField
          id="email"
          label={i18n.t(rk.LABEL_EMAIL_ADDRESS)}
          type="email"
          testid="test-email-field"
          onChange={(e) => { setEmail(e.target.value); clearErrors('email'); }}
          value={email}
          fieldRef={register({
            required: i18n.t(rk.ERROR_EMPTY_EMAIL),
            validate: {
              validateEmail: (value) =>
                /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value) || i18n.t(rk.ERROR_EMAIL_FORMAT),
            },
          }) as any}
          onBlur={async () => { await trigger('email'); }}
          fieldErrors={errors.email?.message}
          hint={i18n.t(rk.TEXT_EMAIL_HINT)}
        />
        <FormField
          id="confirmEmail"
          label={i18n.t(rk.LABEL_CONFIRM_EMAIL_ADDRESS)}
          type="email"
          testid="test-confirm-email-field"
          onChange={(e) => { setConfirmEmail(e.target.value); clearErrors('confirmEmail'); }}
          value={confirmEmail}
          fieldRef={register({
            required: i18n.t(rk.ERROR_EMPTY_EMAIL),
            validate: {
              matchEmail: () => normalizeEmail(email) === normalizeEmail(confirmEmail) || i18n.t(rk.ERROR_EMAIL_MATCH),
            },
          }) as any}
          onBlur={async () => { await trigger('confirmEmail'); }}
          fieldErrors={errors.confirmEmail?.message}
        />
        <CaptchaGate
          onStatusChange={setCaptchaCompleted}
          widgetTestId="myvw-captcha-widget"
          errorTestId="myvw-captcha-error"
        />
      </StepLayout>
    );
  }

  // ── Credentials step ──────────────────────────────────────────────────────

  if (step === 'credentials') {
    return (
      <StepLayout
        title={<h3 className='u-mb-small'>{i18n.t(rk.TITLE_MYVW_REGISTER_FORM)}</h3>}
        primaryAction={{
          label: i18n.t(rk.BUTTON_CANCEL),
          onClick: () => setStep('form'),
          testId: 'register-credentials-cancel',
        }}
        secondaryAction={{
          label: i18n.t(rk.BUTTON_CONTINUE),
          onClick: handleCredentials,
          disabled: !credentialsEnabled,
          testId: 'register-credentials-submit',
        }}
      >
        <h4 className="u-text-subtitle u-mb-xsmall">{i18n.t(rk.LABEL_MYVW_VEHICLE_INFO)}</h4>
        <p className="u-mt">{i18n.t(rk.TEXT_MYVW_REGISTER_CREDENTIALS_INTRO)}</p>
        <p className="u-mt">{i18n.t(rk.TEXT_MYVW_REGISTER_CREDENTIALS_DESC)}</p>
        {usernameConflict && (
          <p className="c-error-message u-mt" data-testid="register-username-conflict">
            {i18n.t(rk.ERROR_MYVW_USERNAME_CONFLICT)}
          </p>
        )}
        {error && <p className="c-error-message u-mt">{error}</p>}
        <p className='u-mt'><b>*All fields are required.</b></p>
        <FormField
          id="username"
          label={i18n.t(rk.LABEL_USERNAME)}
          type="text"
          testid="test-username-field"
          onChange={(e) => { setUsername(e.target.value); clearErrors('username'); }}
          value={username}
          hint={i18n.t(rk.TEXT_USERNAME_HINT)}
          fieldRef={register({
            required: i18n.t(rk.ERROR_EMPTY_USERNAME),
            validate: {
              validateUsername: (value) =>
                validate.IsValidNewUserName(value) || i18n.t(rk.ERROR_API_INVALID_USERNAME),
            },
          }) as any}
          onBlur={async () => { await trigger('username'); }}
          fieldErrors={errors.username?.message}

        />
        <FormField
          id="password"
          label={i18n.t(rk.LABEL_PASSWORD)}
          type="password"
          testid="test-password-field"
          onChange={(e) => { setPassword(e.target.value); clearErrors('password'); }}
          autoCompleteOff
          onCopy={(e) => { e.preventDefault(); return false; }}
          onPaste={(e) => { e.preventDefault(); return false; }}
          value={password}
          hint={i18n.t(rk.TEXT_PASSWORD_HINT)}
          fieldRef={register({
            required: i18n.t(rk.ERROR_EMPTY_PASSWORD),
            validate: {
              validatePassword: () =>
                validate.IsValidPassword(password, username) || i18n.t(rk.ERROR_API_INVALID_PASSWORD),
            },
          }) as any}
          onBlur={async () => { await trigger('password'); }}
          fieldErrors={errors.password?.message}
        />
        <FormField
          id="confirmPassword"
          label={i18n.t(rk.LABEL_CONFIRM_NEW_PASSWORD)}
          type="password"
          testid="test-confirm-password-field"
          onChange={(e) => { setConfirmPassword(e.target.value); clearErrors('confirmPassword'); }}
          autoCompleteOff
          onCopy={(e) => { e.preventDefault(); return false; }}
          onPaste={(e) => { e.preventDefault(); return false; }}
          value={confirmPassword}
          fieldRef={register({
            required: i18n.t(rk.ERROR_EMPTY_CONFIRM_PSWD),
            validate: {
              matchPassword: () => password === confirmPassword || i18n.t(rk.ERROR_PASSWORD_MATCH),
            },
          }) as any}
          onBlur={async () => { await trigger('confirmPassword'); }}
          fieldErrors={errors.confirmPassword?.message}
        />
        <SelectField
          id="challengeQuestion"
          name="challengeQuestion"
          label={i18n.t(rk.LABEL_CHALLENGE_QUESTION)}
          options={challengeOptions}
          placeholder={i18n.t(rk.LABEL_SELECT_CHALLENGE_QUESTION)}
          testid="test-challenge-question-field"
          onChange={(e) => { setChallengeQuestion(e.target.value); clearErrors('challengeQuestion'); }}
          onBlur={() => { trigger('challengeQuestion'); }}
          fieldRef={register({ required: i18n.t(rk.ERROR_EMPTY_CHALLENGE_QUESTION) }) as any}
          fieldErrors={errors.challengeQuestion?.message}
        />
        <FormField
          id="challengeAnswer"
          label={i18n.t(rk.LABEL_CHALLENGE_ANSWER)}
          type="text"
          testid="test-challenge-answer-field"
          onChange={(e) => { setChallengeAnswer(e.target.value); clearErrors('challengeAnswer'); }}
          autoCompleteOff
          value={challengeAnswer}
          hint={i18n.t(rk.TEXT_CHALLENGE_ANSWER_HINT)}
          fieldRef={register({
            required: i18n.t(rk.ERROR_EMPTY_CHALLENGE_ANSWER),
          }) as any}
          onBlur={async () => { await trigger('challengeAnswer'); }}
          fieldErrors={errors.challengeAnswer?.message}
        />
      </StepLayout>
    );
  }

  // ── Consent step ──────────────────────────────────────────────────────────
  
  return (
    <MyVWPaperlessConsent
      email = {email}
      agreeConsent = {agreeConsent}
      loading = {loading}
      error = {error}
      verifyErrorBanner = {verifyErrorBanner}
      onAgreeChange = {setAgreeConsent}
      onContinue = {handleConsent}
      onCancel = {() => setStep('credentials')}
      continueDisabled = {!agreeConsent || loading}
      title = {i18n.t(rk.TITLE_MYVW_REGISTER_FORM)}
      cancelTestId = "register-consent-cancel"
      continueTestId = "register-consent-submit"
      checkboxTestId = "register-consent-checkbox"
      isRegistrationFlow = {true}
    />
  );
};

export default MyVWRegister;